import React, { useState } from 'react';
import { BoxIcon, PencilIcon, PlusIcon, Trash2Icon, UploadCloudIcon, XIcon } from 'lucide-react';
import { CATEGORIES } from '../../data/products';
import { StoreCategory } from '../../types';
const INITIAL: StoreCategory[] = CATEGORIES.map(cat => ({
  id: cat.id,
  name: cat.label,
  description: cat.blurb,
  image: cat.image,
  products: cat.count,
  active: true
}));
const fieldClass = 'w-full border border-white/10 bg-ink-900 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-pink-500 focus:outline-none';
const labelClass = 'block text-[10px] font-bold uppercase tracking-[0.18em] text-white/45';
export function CategoriesSection() {
  const [categories, setCategories] = useState<StoreCategory[]>(INITIAL);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<StoreCategory | null>(null);
  const toggleActive = (id: string) => setCategories(prev => prev.map(c => c.id === id ? {
    ...c,
    active: !c.active
  } : c));
  const remove = (id: string) => setCategories(prev => prev.filter(c => c.id !== id));
  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (category: StoreCategory) => {
    setEditing(category);
    setModalOpen(true);
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const name = String(form.get('name') ?? '').trim();
    const description = String(form.get('description') ?? '').trim();
    if (!name) return;
    if (editing) {
      setCategories(prev => prev.map(c => c.id === editing.id ? {
        ...c,
        name,
        description
      } : c));
    } else {
      setCategories(prev => [...prev, {
        id: `cat-${Date.now()}`,
        name,
        description,
        image: CATEGORIES[0].image,
        products: 0,
        active: true
      }]);
    }
    setModalOpen(false);
  };
  return <>
      <section className="border border-white/8 bg-ink-700" data-id="element-159" data-mp="17jetwjuosk6m">
        <div className="flex flex-col gap-4 border-b border-white/8 p-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-160" data-mp="1cg562w3devmr">
          <div data-id="element-161" data-mp="1hf8duoaqjfyf">
            <h2 className="font-display text-2xl uppercase tracking-wide text-white" data-id="element-162" data-mp="1heq6avimhfb7">
              Categorías
            </h2>
            <p className="mt-1 text-xs text-white/40" data-id="element-163" data-mp="1qgppnuh40vhb">
              {categories.length} categorías ·{' '}
              {categories.filter(c => c.active).length} activas
            </p>
          </div>
          <button type="button" onClick={openCreate} className="flex items-center justify-center gap-2 bg-pink-500 px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-pink-600" data-id="element-164" data-mp="206iy4mlyguhc">
            <PlusIcon className="h-4 w-4" data-id="element-165" data-mp="2954fiefoy3gj" /> Nueva categoría
          </button>
        </div>

        <div className="grid gap-4 p-5 sm:grid-cols-2 xl:grid-cols-3" data-id="element-166" data-mp="2e6jze364u3xc">
          {categories.map(category => <article key={category.id} className="group flex flex-col border border-white/8 bg-ink-800 transition-colors hover:border-pink-500/50" data-id="element-167" data-mp="2gkkd7hh0sj7">
              <div className="relative h-36 overflow-hidden bg-black" data-id="element-168" data-mp="3ppr08u0abill">
                <img src={category.image} alt={category.name} className={`h-full w-full object-cover transition-all duration-500 group-hover:scale-105 ${category.active ? 'opacity-70' : 'opacity-25 grayscale'}`} data-id="element-169" data-mp="3kpv57k9kf1i6" />
                {!category.active && <span className="absolute left-3 top-3 bg-black/80 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white/70" data-id="element-170" data-mp="7ijpalsuzxi9">
                    Inactiva
                  </span>}
              </div>

              <div className="flex flex-1 flex-col p-5" data-id="element-171" data-mp="105xgs4dsg0uz">
                <h3 className="font-display text-xl uppercase leading-none tracking-wide text-white" data-id="element-172" data-mp="jrg2pii4oy1i">
                  {category.name}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-white/45" data-id="element-173" data-mp="orko9mxw8iyl">
                  {category.description}
                </p>
                <p className="mt-4 flex items-center gap-2 text-xs text-white/50" data-id="element-174" data-mp="fpbbsrj2efik">
                  <BoxIcon className="h-3.5 w-3.5 text-pink-500" data-id="element-175" data-mp="frfonyyxyouz" />
                  {category.products}{' '}
                  {category.products === 1 ? 'producto' : 'productos'}
                </p>

                <div className="mt-5 flex items-center justify-between border-t border-white/8 pt-4" data-id="element-176" data-mp="17ilmg8r6wah3">
                  <button type="button" role="switch" aria-checked={category.active} aria-label={`${category.name} ${category.active ? 'activa' : 'inactiva'}`} onClick={() => toggleActive(category.id)} className={`relative h-6 w-11 rounded-full transition-colors ${category.active ? 'bg-pink-500' : 'bg-ink-500'}`} data-id="element-177" data-mp="yogxxvzj3b4q">
                    <span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-all ${category.active ? 'left-6' : 'left-1'}`} data-id="element-178" data-mp="2a8bnxx852lup" />
                  </button>
                  <div className="flex items-center gap-2" data-id="element-179" data-mp="1dj40ukrvg4oo">
                    <button type="button" onClick={() => openEdit(category)} aria-label={`Editar ${category.name}`} className="border border-white/10 p-2 text-white/60 transition-colors hover:border-pink-500 hover:text-pink-500" data-id="element-180" data-mp="1dkx6bwqr0iv3">
                      <PencilIcon className="h-3.5 w-3.5" data-id="element-181" data-mp="2xz7kf7nji58a" />
                    </button>
                    <button type="button" onClick={() => remove(category.id)} aria-label={`Eliminar ${category.name}`} className="border border-white/10 p-2 text-white/60 transition-colors hover:border-red-500 hover:text-red-500" data-id="element-182" data-mp="35znbvlvfibus">
                      <Trash2Icon className="h-3.5 w-3.5" data-id="element-183" data-mp="3jyd86zafc7i3" />
                    </button>
                  </div>
                </div>
              </div>
            </article>)}
        </div>
      </section>

      {modalOpen && <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/75 p-4 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="category-modal-title" data-id="element-184" data-mp="2r8o6ejpw43ff">
          <div className="w-full max-w-xl border border-white/10 bg-ink-700" data-id="element-185" data-mp="23locfp8xrrmh">
            <div className="flex items-center justify-between border-b border-white/8 px-6 py-5" data-id="element-186" data-mp="28k9d1ps3odbg">
              <h2 id="category-modal-title" className="font-display text-2xl uppercase tracking-wide text-white" data-id="element-187" data-mp="210qhyl5l0tmi">
                {editing ? 'Editar categoría' : 'Nueva categoría'}
              </h2>
              <button type="button" onClick={() => setModalOpen(false)} aria-label="Cerrar" className="p-1.5 text-white/50 transition-colors hover:text-white" data-id="element-188" data-mp="1r1995uv19zmb">
                <XIcon className="h-5 w-5" data-id="element-189" data-mp="1i3p56e5sdp80" />
              </button>
            </div>

            <form onSubmit={handleSubmit} data-id="element-190" data-mp="1d92wiir3aarm">
              <div className="space-y-5 p-6" data-id="element-191" data-mp="18bjed7r9tdue">
                <div data-id="element-192" data-mp="18ccfnt7gnb55">
                  <label className={labelClass} htmlFor="cat-name" data-id="element-193" data-mp="13czure05ge57">
                    Nombre de la categoría
                  </label>
                  <input id="cat-name" name="name" required defaultValue={editing?.name} placeholder="Bandas Elásticas" className={`mt-2 ${fieldClass}`} data-id="element-194" data-mp="ye54xs3rlpbl" />
                </div>

                <div data-id="element-195" data-mp="2n6rl1rnwo7rb">
                  <label className={labelClass} htmlFor="cat-desc" data-id="element-196" data-mp="2n7azgyecke0l">
                    Descripción
                  </label>
                  <textarea id="cat-desc" name="description" rows={3} defaultValue={editing?.description} placeholder="Contá qué incluye esta categoría y para qué tipo de entrenamiento sirve." className={`mt-2 ${fieldClass} resize-none`} data-id="element-197" data-mp="2d9wkz4bzhzax" />
                </div>

                <div data-id="element-198" data-mp="1ujexpf9x7vwy">
                  <span className={labelClass} data-id="element-199" data-mp="1uilxyy9dq6ob">Imagen de portada</span>
                  <div className="mt-2 flex flex-col items-center justify-center border border-dashed border-white/15 bg-ink-900 px-6 py-10 text-center transition-colors hover:border-pink-500/60" data-id="element-200" data-mp="1gjm8joe1lnr3">
                    <UploadCloudIcon className="h-7 w-7 text-white/30" data-id="element-201" data-mp="11wxc5vf37qox" />
                    <p className="mt-3 text-sm text-white/60" data-id="element-202" data-mp="11x725j04txum">
                      Arrastrá una imagen acá o{' '}
                      <span className="text-pink-400 underline" data-id="element-203" data-mp="x06y1dmuf565">
                        buscá en tu equipo
                      </span>
                    </p>
                    <p className="mt-1 text-xs text-white/30" data-id="element-204" data-mp="15xr0gpwfz7ic">
                      JPG o PNG horizontal, mínimo 1200 × 800 px
                    </p>
                  </div>
                  {editing && <img src={editing.image} alt="" className="mt-3 h-20 w-32 border border-white/10 object-cover" data-id="element-205" data-mp="1fw6qepjqgaq9" />}
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t border-white/8 px-6 py-5 sm:flex-row sm:justify-end" data-id="element-206" data-mp="23lr87545h1xy">
                <button type="button" onClick={() => setModalOpen(false)} className="border border-white/20 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-white/70 transition-colors hover:border-white hover:text-white" data-id="element-207" data-mp="1yo8yhcib6iow">
                  Cancelar
                </button>
                <button type="submit" className="bg-pink-500 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-pink-600" data-id="element-208" data-mp="2vi90z5x4u3ii">
                  Guardar categoría
                </button>
              </div>
            </form>
          </div>
        </div>}
    </>;
}